import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CompetitionsService } from '../../../core/competitions/competitions';

/** F-015 — owner-only inline editor for a competition's free-text note to
 *  contestants. Extracted out of CompetitionDetail (which was accumulating
 *  too many unrelated concerns) — takes the already-loaded message/owner
 *  state as inputs rather than fetching its own copy, and asks the parent
 *  to reload after a successful save via `changed`, same pattern as
 *  CompetitionEditModal. */
@Component({
  selector: 'app-competition-message',
  imports: [ReactiveFormsModule],
  templateUrl: './competition-message.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-message.css',
})
export class CompetitionMessage {
  private readonly competitions = inject(CompetitionsService);

  readonly competitionId = input.required<number>();
  readonly message = input<string | null>(null);
  readonly isOwner = input(false);
  readonly changed = output<void>();

  readonly editing = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly control = new FormControl('', { nonNullable: true });

  start(): void {
    this.error.set(null);
    this.control.setValue(this.message() ?? '');
    this.editing.set(true);
  }

  cancel(): void {
    this.editing.set(false);
    this.error.set(null);
  }

  async save(): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const message = this.control.value.trim();
      await this.competitions.update(this.competitionId(), { message: message || null });
      this.editing.set(false);
      this.changed.emit();
    } catch (err) {
      this.error.set(this.errorMessageFor(err));
    } finally {
      this.saving.set(false);
    }
  }

  private errorMessageFor(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const message = (err.error as { message?: string } | null)?.message;
      if (message) return message;
    }
    return 'Something went wrong — try again in a moment.';
  }
}
